import React, { useMemo, useState } from 'react';
import { useCombobox } from 'downshift';
import classNames from 'classnames';
import { Label } from 'reactstrap';

import { Icon } from 'js/components';
import RubricListItemMeta from 'js/routes/Rubrics/components/RubricListItemMeta';
import { useStandardCollator } from 'js/utils/string';

import styles from './OrgRubricPicker.module.scss';

function rubricToLabel(rubric) {
  return rubric?.name ?? '';
}

function OrgRubricPicker({
  rubrics = [],
  rubricId = '',
  onRubricChange = () => {},
  loading = false,
  error = null,
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [userTypedThisSession, setUserTypedThisSession] = useState(false);
  const collator = useStandardCollator();

  const sortedRubrics = useMemo(
    () =>
      [...rubrics].sort((a, b) =>
        collator.compare(a?.name ?? '', b?.name ?? '')
      ),
    [rubrics, collator]
  );

  const selectedRubric = useMemo(
    () => sortedRubrics.find((r) => r.id === rubricId) ?? null,
    [sortedRubrics, rubricId]
  );

  const selectedDisplayLabel = rubricToLabel(selectedRubric);

  const filteredRubrics = useMemo(() => {
    if (!userTypedThisSession || !inputValue.trim()) {
      return sortedRubrics;
    }
    const q = inputValue.trim().toLowerCase();
    return sortedRubrics.filter((r) =>
      rubricToLabel(r).toLowerCase().includes(q)
    );
  }, [sortedRubrics, inputValue, userTypedThisSession]);

  const isDisabled = disabled || (loading && sortedRubrics.length === 0);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: filteredRubrics,
    selectedItem: selectedRubric,
    itemToString: rubricToLabel,
    inputValue: isFocused ? inputValue : selectedDisplayLabel || inputValue,
    onInputValueChange: ({ inputValue: next, type }) => {
      if (type === useCombobox.stateChangeTypes.InputChange) {
        setUserTypedThisSession(true);
      }
      setInputValue(next ?? '');
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem?.id) {
        onRubricChange(selectedItem.id);
      }
    },
    onStateChange: ({ type, selectedItem }) => {
      if (
        type === useCombobox.stateChangeTypes.ToggleButtonClick ||
        type === useCombobox.stateChangeTypes.FunctionOpenMenu
      ) {
        setUserTypedThisSession(false);
        setInputValue(selectedDisplayLabel);
        setIsFocused(true);
      }

      if (type === useCombobox.stateChangeTypes.ItemClick && selectedItem) {
        setUserTypedThisSession(false);
        setInputValue('');
      }

      if (type === useCombobox.stateChangeTypes.FunctionCloseMenu) {
        setUserTypedThisSession(false);
        setInputValue('');
      }
    },
  });

  const displayValue = isFocused ? inputValue : selectedDisplayLabel;

  const downshiftInputProps = getInputProps({
    id: 'org-manage-rubric-picker',
    disabled: isDisabled,
    placeholder: loading
      ? 'Loading rubrics…'
      : sortedRubrics.length === 0
      ? 'No rubrics in this workspace'
      : 'Select or type to search rubrics…',
  });

  const inputProps = {
    ...downshiftInputProps,
    value: displayValue,
    onFocus: (e) => {
      setUserTypedThisSession(false);
      setIsFocused(true);
      setInputValue(selectedDisplayLabel);
      downshiftInputProps.onFocus?.(e);
    },
    onBlur: (e) => {
      setIsFocused(false);
      setInputValue('');
      downshiftInputProps.onBlur?.(e);
    },
  };

  return (
    <div className={styles.root}>
      <Label for="org-manage-rubric-picker" className="mb-1">
        Rubric
      </Label>
      <div className={classNames('combobox', styles.root)}>
        <div className="input-group">
          <input
            className={classNames('form-control', {
              'is-invalid': Boolean(error),
            })}
            {...inputProps}
          />
          <div className="input-group-append">
            <span
              aria-label="toggle rubric menu"
              type="button"
              {...getToggleButtonProps({ disabled: isDisabled })}
            >
              {isOpen ? (
                <Icon icon={['far', 'chevron-up']} fixedWidth />
              ) : (
                <Icon icon={['far', 'chevron-down']} fixedWidth />
              )}
            </span>
          </div>
        </div>
        <ul
          {...getMenuProps()}
          className={classNames('combobox-items', styles.menu)}
          style={!isOpen ? { display: 'none' } : {}}
        >
          {isOpen &&
            isFocused &&
            (filteredRubrics.length === 0 ? (
              <li className="combobox-item" key="no-results">
                {inputValue.trim()
                  ? 'No matching rubrics'
                  : 'No rubrics in this workspace'}
              </li>
            ) : (
              filteredRubrics.map((rubric, index) => (
                <li
                  className={classNames(
                    'combobox-item',
                    styles['picker-item'],
                    {
                      highlighted: highlightedIndex === index,
                      selected: selectedRubric?.id === rubric.id,
                    }
                  )}
                  key={rubric.id}
                  {...getItemProps({ item: rubric, index })}
                >
                  <div className={styles['picker-item-name']}>
                    {rubric.name}
                  </div>
                  <div className={styles['picker-item-meta']}>
                    <RubricListItemMeta rubric={rubric} />
                  </div>
                </li>
              ))
            ))}
        </ul>
      </div>
      {error ? <p className="text-danger small mb-0 mt-1">{error}</p> : null}
      {!loading && !error && sortedRubrics.length === 0 ? (
        <p className={classNames(styles['empty-hint'], 'mb-0 mt-1')}>
          Create a rubric in this workspace to configure org weight schemes.
        </p>
      ) : null}
    </div>
  );
}

export default OrgRubricPicker;
